import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Logger } from 'projects/common/src/app/classes';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableExtraParams } from 'projects/common/src/app/interfaces/table/table-extra-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { ITableLargeColumnConfig } from 'projects/common/src/app/interfaces/table/table-large-column-config';
import { CacheService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { objectPathExists } from 'projects/common/src/app/services/utils/utilities';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { FilesService } from 'src/app/modules/files/services/files/files.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { IFrontReportParams } from '../../interfaces/report-fronts';
import { ReportFrontService } from '../../services/report-fronts.service';
import { ReportFrontModalComponent } from './report-front-modal/report-front-modal.component';

@Component({
  selector: 'app-report-fronts',
  templateUrl: './report-fronts.component.html',
  styleUrls: ['./report-fronts.component.scss'],
})
export class ReportFrontsComponent extends Logger implements OnInit {
  public tableConfig: ITableConfig;
  public message: string;
  public isActiveModal = false;
  public selectedFilters: IOutputFilter;
  public modalOpened = false;
  public systemError: string;
  public filterConfig: IFilterConfig;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  constructor(
    private cacheService: CacheService,
    private languageService: LanguageService,
    private tableService: TableConfigService,
    private layoutConfigService: LayoutConfigService,
    private modalService: NgbModal,
    private fileService: FilesService,
    private progressBarService: BooleanStateService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
    private _frontService: ReportFrontService,
  ) {
    super();
    this.filterConfig = {
      dates: { required: true },
      shift: { required: true },
      zone: { required: true, multiselect: true },
      front: { required: true, multiselect: true },
      cleanButton: true,
    };
    this.disabledExcelBtn = false;
  }

  /**
   * On init component.
   */
  public ngOnInit(): void {
    this.setHeaderSection();
  }

  /**
   * Get fronts data and set table config.
   *
   * @param selectedFilters: Data for filter: startDate, endDate, frontList and zoneList.
   */
  public async getReportFrontData(selectedFilters: IOutputFilter): Promise<void> {
    this.progressBarService.start();
    this.message = undefined;
    this.tableConfig = undefined;
    this.selectedFilters = selectedFilters;
    const params = this.buildParams(selectedFilters);

    try {
      const data = await this._frontService.getFrontsData(params);
      if (data && data.length) {
        this.setTableConfig(data);
      } else {
        this.message = this.languageService.message('m034');
      }
      this.systemError = '';
    } catch ({ name }) {
      this.systemError = name === 'TimeoutError' ? 'm014' : 'm017';
    }
    this.progressBarService.stop();
    const filters = {
      reports: 'report-checklist',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * Map data for table
   *
   * @param tableData: Data of fronts.
   */
  public setTableConfig(tableData: any[]): void {
    const sortables = ['dateInitEvent', 'dateEndEvent', 'zoneName', 'frontName', 'stage', 'statusStage'];
    const excludeColumns = ['image'];

    const customLargeColumns: ITableLargeColumnConfig[] = [
      { field: 'dateInitEvent', isLargeColumn: true, flexGrow: 0.4 },
      { field: 'dateEndEvent', isLargeColumn: true, flexGrow: 0.4 },
      { field: 'shiftName', isLargeColumn: true, flexGrow: 0.2 },
      { field: 'zoneName', isLargeColumn: true, flexGrow: 0.2 },
      { field: 'frontName', isLargeColumn: true, flexGrow: 0.4 },
      { field: 'stage', isLargeColumn: true, flexGrow: 0.2 },
      { field: 'statusStage', isLargeColumn: true, flexGrow: 0.3 },
      { field: 'interactionType', isLargeColumn: true, flexGrow: 0.45 },
      { field: 'Detail', isLargeColumn: true, flexGrow: 1.1 },
      { field: 'userFullName', isLargeColumn: true, flexGrow: 0.3 },
    ];

    const extraData = {
      field: 'interactionType',
      data: 'image',
    };

    const headerParams: ITableHeaderParams = {
      data: tableData,
      replaceColumnName: [
        { prevName: 'dateInitEvent', newName: 'date-init' },
        { prevName: 'dateEndEvent', newName: 'date-end' },
        { prevName: 'zoneName', newName: 'zone' },
        { prevName: 'frontName', newName: 'front' },
        { prevName: 'statusStage', newName: 'stage-state' },
        { prevName: 'interactionType', newName: 'type-interaction' },
        { prevName: 'userFullName', newName: 'responsible' },
      ],
      sortables,
      customLargeColumns,
      excludeColumns,
    };

    const datasetParams: ITableDataParams = {
      tableData,
      clickConfig: [{ field: 'interactionType', hasCursorPointer: false, hasBoldHover: false }],
      customLargeColumns,
      excludeColumns,
      extraData,
    };

    const extraParams: ITableExtraParams = {
      extraWide: true,
    };

    const header = this.tableService.setHeaderConfig(headerParams);
    const dataset = this.tableService.setDatasetConfig(datasetParams);

    this.tableConfig = { header, dataset, extraParams };
  }

  /**
   * Set titles of headers
   */
  private setHeaderSection(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'fronts-report',
      imageKey: 'report',
      imageType: 'section',
    };
    this.layoutConfigService.setConfig(config);
  }

  /**
   * Function to show image in a modal
   */
  public async viewImage($event): Promise<void> {
    if (objectPathExists($event, 'cell.extraData')) {
      const fileGuid = $event.cell.extraData;
      const date = $event.row.find((data) => data.key === 'dateInitEvent');
      const userFullName = $event.row.find((data) => data.key === 'userFullName');
      const detail = $event.row.find((data) => data.key === 'detail');
      const front = $event.row.find((data) => data.key === 'frontName');
      try {
        const image = await this.fileService.downloadFrontImage(fileGuid);
        const componentRef = this.modalService.open(ReportFrontModalComponent, { centered: true, windowClass: 'modal-width-40' });
        componentRef.componentInstance.image = image;
        componentRef.componentInstance.fileGuid = fileGuid;
        componentRef.componentInstance.title = front && front.value;
        componentRef.componentInstance.date = date && date.value;
        componentRef.componentInstance.userFullName = userFullName && userFullName.value;
        componentRef.componentInstance.detail = detail && detail.value;
      } catch (error) {
        this.error(error);
      }
    }
  }

  /**
   * Function to build params for http requests.
   *
   * @param selectedFilters: Options filter selected.
   */
  public buildParams(selectedFilters: IOutputFilter): IFrontReportParams {
    const { startDate } = selectedFilters;
    const { endDate } = selectedFilters;
    const { shiftId } = selectedFilters;
    const frontList = selectedFilters.frontId;
    const zoneList = selectedFilters.zoneId;
    const companyId = this.cacheService.local('session').user.company.id;

    const params = {
      startDate,
      endDate,
      shiftId,
      frontList,
      zoneList,
      companyId,
    };

    return params;
  }

  /**
   * Download excel file for report fronts
   */
  public async downloadExcelFile(): Promise<void> {
    const selectRangeDate = {
      startDate: this.selectedFilters.startDate,
      endDate: this.selectedFilters.endDate,
    };
    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    this._frontService.getStatusFrontReportDocument(this.selectedFilters).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'fronts-report-excel-filename', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }
}
